import React, { useEffect, useMemo, useState } from 'react';
import AdminPageShell from "../components/AdminPageShell";
import API from '../../../util/API';

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const ALL_PERMISSIONS = [
  { key: 'admin.read', label: 'Admin Read' },
  { key: 'admin.write', label: 'Admin Write' },
  { key: 'role.read', label: 'Role Read' },
  { key: 'role.write', label: 'Role Write' },
  { key: 'logs.read', label: 'Logs Read' },
  { key: 'orders.view', label: 'Orders View' },
  { key: 'orders.manage', label: 'Orders Manage' },
  { key: 'products.view', label: 'Products View' },
  { key: 'products.manage', label: 'Products Manage' },
  { key: 'users.manage', label: 'Users Manage' },
];

const RolesPermissions = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const [create, setCreate] = useState({ name: '', description: '', permissions: [] });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', description: '', permissions: [] });

  const permissionKeys = useMemo(() => ALL_PERMISSIONS.map((p) => p.key), []);

  const load = async () => {
    setError('');
    setOk('');
    setLoading(true);
    try {
      const res = await API.get('/role');
      setRoles(res?.data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const togglePermission = (target, perm) => {
    const current = Array.isArray(target.permissions) ? target.permissions : [];
    const next = current.includes(perm) ? current.filter((p) => p !== perm) : [...current, perm];
    return { ...target, permissions: next };
  };

  const startEdit = (role) => {
    setError('');
    setOk('');
    setEditingId(role._id);
    setEditDraft({
      name: role.name || '',
      description: role.description || '',
      permissions: Array.isArray(role.permissions) ? role.permissions : [],
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ name: '', description: '', permissions: [] });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    setError('');
    setOk('');
    try {
      await API.put(`/role/${editingId}`, {
        name: editDraft.name,
        description: editDraft.description,
        permissions: editDraft.permissions,
      });
      setOk('Role updated');
      cancelEdit();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (role) => {
    if (!role?._id) return;
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    setSaving(true);
    setError('');
    setOk('');
    try {
      await API.delete(`/role/${role._id}`);
      setOk('Role deleted');
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  const createRole = async () => {
    const name = String(create.name || '').trim();
    if (!name) {
      setError('Role name is required');
      return;
    }
    setSaving(true);
    setError('');
    setOk('');
    try {
      await API.post('/role', {
        name,
        description: create.description,
        permissions: create.permissions,
      });
      setOk('Role created');
      setCreate({ name: '', description: '', permissions: [] });
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageShell
      title="Roles & Permissions"
      description="Control which permissions are enabled for each role."
    >
      <Card className="p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Role Name</label>
            <input
              value={create.name}
              onChange={(e) => setCreate({ ...create, name: e.target.value })}
              placeholder="e.g. Manager"
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Description</label>
            <input
              value={create.description}
              onChange={(e) => setCreate({ ...create, description: e.target.value })}
              placeholder="Short description…"
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Permissions</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {ALL_PERMISSIONS.map((p) => (
              <label key={p.key} className="flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30"
                  checked={create.permissions.includes(p.key)}
                  onChange={() => setCreate(togglePermission(create, p.key))}
                />
                <span className="font-semibold">{p.label}</span>
                <span className="text-xs text-slate-500">({p.key})</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={createRole}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors disabled:opacity-60"
          >
            Create Role
          </button>
          <button
            type="button"
            onClick={load}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
          >
            Refresh
          </button>
        </div>

        {error ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        ) : null}
        {ok ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {ok}
          </div>
        ) : null}
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-black tracking-wide text-slate-600 uppercase">
                <th className="py-3 px-4">Role</th>
                <th className="py-3 px-4">Description</th>
                <th className="py-3 px-4">Permissions</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-sm text-slate-500">Loading…</td>
                </tr>
              ) : roles?.length ? (
                roles.map((role) => {
                  const isEditing = editingId === role._id;
                  const perms = isEditing ? editDraft.permissions : role.permissions;
                  const safePerms = Array.isArray(perms) ? perms : [];
                  const unknownPerms = safePerms.filter((p) => !permissionKeys.includes(p) && p !== '*');
                  return (
                    <tr key={role._id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <input
                            value={editDraft.name}
                            onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
                          />
                        ) : (
                          <div className="font-semibold text-slate-900">{role.name}</div>
                        )}
                        {role.isSystem ? (
                          <div className="text-xs text-slate-500">System role</div>
                        ) : null}
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {isEditing ? (
                          <input
                            value={editDraft.description}
                            onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
                          />
                        ) : (
                          role.description || '—'
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-2">
                          {ALL_PERMISSIONS.map((p) => (
                            <label
                              key={p.key}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700"
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30"
                                checked={safePerms.includes(p.key)}
                                onChange={() =>
                                  isEditing
                                    ? setEditDraft(togglePermission(editDraft, p.key))
                                    : null
                                }
                                disabled={!isEditing || role.isSystem || saving}
                              />
                              <span className="font-semibold">{p.key}</span>
                            </label>
                          ))}
                          {unknownPerms.length ? (
                            <span className="text-xs text-slate-500">+{unknownPerms.length} custom</span>
                          ) : null}
                          {safePerms.includes('*') ? (
                            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-[#D2042D]">
                              *
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {role.isSystem ? (
                          <span className="text-xs text-slate-500">Locked</span>
                        ) : isEditing ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={saveEdit}
                              disabled={saving}
                              className="inline-flex items-center rounded-2xl px-3 py-1.5 text-xs font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors disabled:opacity-60"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={saving}
                              className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => startEdit(role)}
                              className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#D2042D] transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteRole(role)}
                              className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#D2042D] transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-sm text-slate-500">No roles found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminPageShell>
  );
};

export default RolesPermissions; 